// ============================================
// SELECTORMAPA.JS — Captura de ubicación sin Google Maps API
// Soporta: coordenadas, URLs de Google Maps (larga/corta), Plus Codes.
// Preview con tile estático de OpenStreetMap (gratis).
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import { OpenLocationCode } from 'open-location-code';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';

const COLOR_EXITO = '#1D9E75';
const COLOR_NARANJA = '#e88b3f';
const olc = new OpenLocationCode();

// ============================================
// PARSER CLIENT-SIDE
// ============================================

function parseUbicacion(textoRaw) {
  if (!textoRaw) return null;
  const texto = String(textoRaw).trim();
  if (!texto) return null;

  // 1. URLs largas de Google Maps — 3 patrones en orden de preferencia
  const patronesURL = [
    { re: /@(-?\d+\.?\d*),(-?\d+\.?\d*)/, fuente: 'url_larga' },
    { re: /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/, fuente: 'url_q' },
    { re: /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/, fuente: 'url_interna' },
  ];
  for (const { re, fuente } of patronesURL) {
    const m = texto.match(re);
    if (m) {
      const lat = parseFloat(m[1]);
      const lng = parseFloat(m[2]);
      if (coordsValidas(lat, lng)) return { lat, lng, fuente };
    }
  }

  // 2. Plus Code completo (sin contexto de lugar)
  //    Formato: 8 caracteres + "+" + 2-3 caracteres (ej: 849VCWC8+R9)
  //    Lo detectamos antes de las coordenadas porque no tiene espacios ni "," .
  const plusCodeCompleto = texto.replace(/\s+/g, '');
  if (olc.isValid(plusCodeCompleto) && olc.isFull(plusCodeCompleto)) {
    try {
      const area = olc.decode(plusCodeCompleto);
      return { lat: area.latitudeCenter, lng: area.longitudeCenter, fuente: 'plus_code' };
    } catch { /* cae a los siguientes casos */ }
  }

  // 3. Coordenadas sueltas con formato flexible
  //    Admite: "19.4326, -99.1332", "19.4326 -99.1332", "19.43° N, 99.13° W"
  const mCoord = texto.match(/(-?\d+\.?\d*)[°\s,NSns]+(-?\d+\.?\d*)/);
  if (mCoord) {
    let lat = parseFloat(mCoord[1]);
    let lng = parseFloat(mCoord[2]);
    // Si el usuario escribió "99.13, 19.43" (lng primero), intercambiamos
    if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
      [lat, lng] = [lng, lat];
    }
    // Aplicar signo según N/S/E/W si están presentes
    if (/[sS]/.test(texto.slice(mCoord.index, mCoord.index + mCoord[0].length))) lat = -Math.abs(lat);
    if (/[wW]/.test(texto.slice(mCoord.index, mCoord.index + mCoord[0].length))) lng = -Math.abs(lng);
    if (coordsValidas(lat, lng)) return { lat, lng, fuente: 'coordenadas' };
  }

  return null;
}

function coordsValidas(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng)
    && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

// Detecta si el texto parece algo que el backend podría resolver:
// link corto o plus code corto con ciudad.
function necesitaBackend(texto) {
  const t = String(texto || '').trim();
  if (!t) return false;
  if (/goo\.gl\/maps/i.test(t)) return true;
  if (/maps\.app\.goo\.gl/i.test(t)) return true;
  // Plus code con espacio y palabra después (ej: "7RMP+C3 Ciudad de México")
  if (/\b[23456789CFGHJMPQRVWX]{4,}\+[23456789CFGHJMPQRVWX]{2,}\s+\S+/i.test(t)) return true;
  return false;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export default function SelectorMapa({
  latitudInicial, longitudInicial,
  onGuardar,    // ({ latitud, longitud }) => void
  onCancelar,   // opcional — si se provee, componente actúa como modal
  titulo = '📍 Ubicación',
  // Compatibilidad con nombres viejos
  latitud, longitud, direccion,
}) {
  const latIni = latitudInicial != null ? latitudInicial : latitud;
  const lngIni = longitudInicial != null ? longitudInicial : longitud;

  const [texto, setTexto] = useState('');
  const [detectada, setDetectada] = useState(
    latIni != null && lngIni != null ? { lat: Number(latIni), lng: Number(lngIni), fuente: 'previa' } : null
  );
  const [resolviendo, setResolviendo] = useState(false);
  const [error, setError] = useState(null);
  const [tutorial, setTutorial] = useState(false);
  const [geoCargando, setGeoCargando] = useState(false);
  const [geoDatos, setGeoDatos] = useState(null); // { direccion, colonia, ciudad, estado, codigo_postal } | null
  const [autocompletar, setAutocompletar] = useState(true);
  const [puedeClipboard] = useState(
    typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.readText
  );
  const timerRef = useRef(null);
  const geoTokenRef = useRef(0); // descarta respuestas viejas si cambia la ubicación

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!texto.trim()) {
      setError(null);
      return;
    }
    timerRef.current = setTimeout(async () => {
      const local = parseUbicacion(texto);
      if (local) {
        setDetectada({ ...local, lat: Number(local.lat), lng: Number(local.lng) });
        setError(null);
        return;
      }
      if (necesitaBackend(texto)) {
        await resolverEnBackend(texto);
      } else {
        setDetectada(null);
        setError('No pude detectar la ubicación. Verifica que sea un formato válido.');
      }
    }, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [texto]);

  const resolverEnBackend = async (t) => {
    setResolviendo(true); setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/resolver-ubicacion`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: t }),
      }).then(x => x.json());
      if (r.ok && coordsValidas(Number(r.latitud), Number(r.longitud))) {
        setDetectada({ lat: Number(r.latitud), lng: Number(r.longitud), fuente: r.fuente });
      } else {
        setDetectada(null);
        setError(r.error || 'No pude resolver la ubicación.');
      }
    } catch {
      setDetectada(null);
      setError('No pude conectar para resolver el link. Intenta con coordenadas directas.');
    } finally {
      setResolviendo(false);
    }
  };

  const pegar = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      setTexto(clip);
    } catch {
      setError('No pude leer el portapapeles. Pega manualmente.');
    }
  };

  const limpiar = () => {
    setTexto(''); setDetectada(null); setError(null);
    setGeoDatos(null); setGeoCargando(false);
  };

  // Geocodificación inversa: cuando se detecta una ubicación válida, pedir dirección al backend
  useEffect(() => {
    if (!detectada) { setGeoDatos(null); return; }
    const token = ++geoTokenRef.current;
    setGeoCargando(true);
    setGeoDatos(null);
    fetch(`${API_BASE_CLI}/api/clientes/geocodificar?lat=${detectada.lat}&lng=${detectada.lng}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(j => {
        if (token !== geoTokenRef.current) return; // cambiaron las coords mientras tanto
        if (j.ok) setGeoDatos(j);
        else setGeoDatos(null);
      })
      .catch(() => { if (token === geoTokenRef.current) setGeoDatos(null); })
      .finally(() => { if (token === geoTokenRef.current) setGeoCargando(false); });
  }, [detectada && detectada.lat, detectada && detectada.lng]);

  const guardar = () => {
    if (!detectada) return;
    const payload = { latitud: detectada.lat, longitud: detectada.lng };
    if (geoDatos && autocompletar) {
      if (geoDatos.direccion) payload.direccion = geoDatos.direccion;
      if (geoDatos.colonia) payload.colonia = geoDatos.colonia;
      if (geoDatos.ciudad) payload.ciudad = geoDatos.ciudad;
      if (geoDatos.estado) payload.estado = geoDatos.estado;
      if (geoDatos.codigo_postal) payload.codigo_postal = geoDatos.codigo_postal;
    }
    onGuardar(payload);
  };

  const esModal = !!onCancelar;
  const contenido = (
    <div style={{
      background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 520,
      fontFamily: PP, color: COLOR_GRIS, boxSizing: 'border-box',
    }} onClick={(e) => e.stopPropagation()}>
      <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>{titulo}</div>

      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 10, lineHeight: 1.5 }}>
        Pega desde Google Maps:<br />
        · Coordenadas <code style={codeStyle}>19.4326, -99.1332</code><br />
        · Plus Code <code style={codeStyle}>7RMP+C3 CDMX</code><br />
        · Link compartido <code style={codeStyle}>https://goo.gl/…</code>
      </div>

      <div style={{ position: 'relative' }}>
        <input
          autoFocus
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Pega aquí la ubicación…"
          style={{
            width: '100%', padding: puedeClipboard ? '10px 88px 10px 12px' : '10px 12px',
            border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, fontSize: 13,
            fontFamily: PP, outline: 'none', boxSizing: 'border-box',
          }}
        />
        {puedeClipboard && (
          <button
            onClick={pegar}
            style={{
              position: 'absolute', top: 5, right: 5,
              padding: '6px 10px', background: COLOR_PRINCIPAL, color: '#fff',
              border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', fontFamily: PP,
            }}
          >📋 Pegar</button>
        )}
      </div>

      <div style={{ marginTop: 8 }}>
        <button
          onClick={() => setTutorial(true)}
          style={{
            background: 'transparent', border: 'none', color: COLOR_PRINCIPAL,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: PP,
          }}
        >
          ¿Cómo obtener? →
        </button>
      </div>

      {resolviendo && (
        <div style={{ marginTop: 12, fontSize: 12, color: COLOR_GRIS_CLARO }}>Resolviendo link…</div>
      )}

      {error && !detectada && !resolviendo && (
        <div style={{
          marginTop: 12, padding: 10, background: '#fde8e8', borderRadius: 8,
          fontSize: 12, color: '#8e3838',
        }}>
          ⚠️ {error}
        </div>
      )}

      {detectada && (
        <div style={{ marginTop: 14 }}>
          <div style={{
            padding: '8px 12px', background: '#e8faf5', borderRadius: 8,
            fontSize: 12, color: COLOR_EXITO, fontWeight: 600, marginBottom: 10,
          }}>
            ✅ Detectado: {etiquetaFuente(detectada.fuente)}
          </div>
          <MiniMapa lat={detectada.lat} lng={detectada.lng} />
          <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 10 }}>
            Coordenadas: <strong>{detectada.lat.toFixed(6)}, {detectada.lng.toFixed(6)}</strong>
          </div>
          <div style={{ marginTop: 10 }}>
            <a
              href={`https://www.google.com/maps/?q=${detectada.lat},${detectada.lng}`}
              target="_blank" rel="noreferrer"
              style={{
                display: 'inline-block', padding: '6px 12px', border: `1px solid ${COLOR_BORDE}`,
                borderRadius: 6, fontSize: 12, color: COLOR_GRIS, textDecoration: 'none',
                fontFamily: PP,
              }}
            >
              🗺️ Abrir en Google Maps
            </a>
          </div>

          {geoCargando && (
            <div style={{
              marginTop: 12, padding: '10px 12px', background: '#eaf2fb', borderRadius: 8,
              fontSize: 12, color: COLOR_OSCURO,
            }}>
              ⏳ Buscando información de dirección…
            </div>
          )}

          {!geoCargando && geoDatos && (
            <div style={{
              marginTop: 12, padding: 14, background: '#eaf2fb', borderRadius: 8,
              border: `1px solid #c9d6e3`,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>
                📍 Dirección encontrada:
              </div>
              <div style={{ display: 'grid', gap: 4, fontSize: 12, color: COLOR_GRIS }}>
                {geoDatos.direccion && <CampoGeo etiqueta="Dirección" valor={geoDatos.direccion} />}
                {geoDatos.colonia && <CampoGeo etiqueta="Colonia" valor={geoDatos.colonia} />}
                {geoDatos.ciudad && <CampoGeo etiqueta="Ciudad" valor={geoDatos.ciudad} />}
                {geoDatos.estado && <CampoGeo etiqueta="Estado" valor={geoDatos.estado} />}
                {geoDatos.codigo_postal && <CampoGeo etiqueta="CP" valor={geoDatos.codigo_postal} />}
              </div>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
                fontSize: 12, color: COLOR_OSCURO, cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={autocompletar}
                  onChange={(e) => setAutocompletar(e.target.checked)}
                />
                Autocompletar estos campos al guardar
              </label>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
        {esModal && (
          <button
            onClick={onCancelar}
            style={{
              padding: '8px 16px', border: `1px solid ${COLOR_BORDE}`, background: '#fff',
              borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: PP,
            }}
          >
            Cancelar
          </button>
        )}
        {detectada && (
          <button
            onClick={limpiar}
            style={{
              padding: '8px 16px', border: `1px solid ${COLOR_BORDE}`, background: '#fff',
              borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: PP, color: COLOR_GRIS,
            }}
          >
            ❌ Cambiar
          </button>
        )}
        <button
          onClick={guardar}
          disabled={!detectada}
          style={{
            padding: '8px 18px', border: 'none',
            background: detectada ? COLOR_PRINCIPAL : '#c9d6e3',
            color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: detectada ? 'pointer' : 'not-allowed', fontFamily: PP,
          }}
        >
          💾 Guardar ubicación
        </button>
      </div>

      {tutorial && <TutorialUbicacion onCerrar={() => setTutorial(false)} />}
    </div>
  );

  if (!esModal) return contenido;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.45)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onCancelar}
    >
      {contenido}
    </div>
  );
}

const codeStyle = {
  background: '#f3f4f4', padding: '1px 6px', borderRadius: 4,
  fontSize: 11, fontFamily: 'monospace',
};

function etiquetaFuente(fuente) {
  return {
    coordenadas: 'coordenadas',
    url_larga: 'link de Google Maps',
    url_q: 'link de Google Maps',
    url_interna: 'link de Google Maps',
    link_corto_resuelto: 'link corto de Google Maps',
    plus_code: 'Plus Code',
    previa: 'ubicación guardada',
  }[fuente] || 'ubicación';
}

// ============================================
// MINI-MAPA CON IFRAME DE OSM EMBED
// ============================================

export function MiniMapa({ lat, lng, alto = 300 }) {
  const delta = 0.005;
  const bbox = `${lng - delta}%2C${lat - delta}%2C${lng + delta}%2C${lat + delta}`;
  const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
  return (
    <iframe
      src={embedUrl}
      title="Vista previa"
      loading="lazy"
      style={{
        width: '100%', height: alto + 'px',
        border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
      }}
    />
  );
}

function CampoGeo({ etiqueta, valor }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ minWidth: 80, color: COLOR_GRIS_CLARO }}>{etiqueta}:</div>
      <div style={{ flex: 1, fontWeight: 500 }}>{valor}</div>
    </div>
  );
}

// ============================================
// TUTORIAL INLINE
// ============================================

function TutorialUbicacion({ onCerrar }) {
  const [metodo, setMetodo] = useState(1);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onCerrar}
    >
      <div
        style={{
          background: '#fff', borderRadius: 14, padding: 0, width: '100%', maxWidth: 560,
          fontFamily: PP, color: COLOR_GRIS, maxHeight: 'calc(var(--beep-vh) * 90)', overflowY: 'auto',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px 12px', borderBottom: `1px solid ${COLOR_BORDE}`,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
            Cómo copiar una ubicación desde Google Maps
          </div>
          <button
            onClick={onCerrar}
            style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: COLOR_GRIS_CLARO }}
            title="Cerrar"
          >×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          {[
            { id: 1, label: '📍 Coordenadas' },
            { id: 2, label: '💠 Plus Code' },
            { id: 3, label: '🔗 Link' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setMetodo(t.id)}
              style={{
                flex: 1, padding: '12px 8px', background: 'transparent',
                border: 'none', borderBottom: metodo === t.id ? `3px solid ${COLOR_PRINCIPAL}` : '3px solid transparent',
                color: metodo === t.id ? COLOR_OSCURO : COLOR_GRIS_CLARO,
                fontWeight: metodo === t.id ? 700 : 500, cursor: 'pointer', fontFamily: PP,
                fontSize: 12,
              }}
            >{t.label}</button>
          ))}
        </div>

        <div style={{ padding: 24 }}>
          {metodo === 1 && <MetodoCoordenadas />}
          {metodo === 2 && <MetodoPlusCode />}
          {metodo === 3 && <MetodoLink />}

          <div style={{
            marginTop: 20, padding: 12, background: '#fff8ed', borderRadius: 8,
            fontSize: 12, color: '#8a6d1e', lineHeight: 1.5,
          }}>
            💡 <strong>Tip rápido:</strong> click derecho sobre el mapa → click en las coordenadas (se copian automáticamente).
          </div>
        </div>

        <div style={{ padding: '12px 24px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onCerrar}
            style={{
              padding: '10px 20px', background: COLOR_PRINCIPAL, color: '#fff', border: 'none',
              borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
            }}
          >
            ✅ Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

function Paso({ n, children }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
      <div style={{
        flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
        background: COLOR_PRINCIPAL, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
      }}>{n}</div>
      <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

function MetodoCoordenadas() {
  return (
    <div>
      <Paso n={1}>Abre Google Maps y busca el lugar.</Paso>
      <Paso n={2}>En la barra de direcciones verás algo así: <code style={codeStyle}>google.com/maps/place/.../@19.2835,-99.1647,16z/...</code></Paso>
      <Paso n={3}>Los 2 números después del <strong>@</strong> son las coordenadas.</Paso>
      <Paso n={4}>O haz <strong>click derecho</strong> sobre el pin → aparece un menú.</Paso>
      <Paso n={5}>La primera opción son las coordenadas, click y se copian.</Paso>
      <Paso n={6}>Pega aquí en el formulario.</Paso>
      <div style={{ marginTop: 14 }}><SVGBarraDirecciones /></div>
    </div>
  );
}

function MetodoPlusCode() {
  return (
    <div>
      <Paso n={1}>Abre Google Maps y busca el lugar.</Paso>
      <Paso n={2}>Baja en el panel izquierdo hasta ver un ícono de cuadro con diamante 💠.</Paso>
      <Paso n={3}>Aparece algo como <code style={codeStyle}>7RMP+C3 Ciudad de México</code>.</Paso>
      <Paso n={4}>Copia esa línea completa.</Paso>
      <Paso n={5}>Pega aquí.</Paso>
      <div style={{ marginTop: 14 }}><SVGPanelPlusCode /></div>
    </div>
  );
}

function MetodoLink() {
  return (
    <div>
      <Paso n={1}>Abre Google Maps y busca el lugar.</Paso>
      <Paso n={2}>Click en el botón <strong>Compartir</strong>.</Paso>
      <Paso n={3}>Copia el link que aparece: <code style={codeStyle}>goo.gl/maps/...</code> o <code style={codeStyle}>maps.app.goo.gl/...</code></Paso>
      <Paso n={4}>Pega aquí.</Paso>
      <div style={{ marginTop: 14 }}><SVGBotonCompartir /></div>
    </div>
  );
}

// ============================================
// SVGs ILUSTRATIVOS — inline, minimalistas
// ============================================

function SVGBarraDirecciones() {
  return (
    <svg width="100%" viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: 360 }}>
      <rect width="320" height="180" fill="#f3f4f4" rx="8" />
      <rect x="20" y="30" width="280" height="28" fill="#fff" stroke="#d0d4d8" rx="14" />
      <text x="32" y="48" fontFamily="Poppins, sans-serif" fontSize="9" fill="#4e5357">
        google.com/maps/place/Beep/
      </text>
      <text x="145" y="48" fontFamily="Poppins, sans-serif" fontSize="9" fill="#e88b3f" fontWeight="700">
        @19.2835,-99.1647
      </text>
      <text x="215" y="48" fontFamily="Poppins, sans-serif" fontSize="9" fill="#4e5357">,16z/data=...</text>
      <path d="M 190 66 L 190 85 L 180 80 M 190 85 L 200 80" stroke="#e88b3f" strokeWidth="2" fill="none" strokeLinecap="round" />
      <text x="150" y="110" fontFamily="Poppins, sans-serif" fontSize="11" fill="#2f5f9e" fontWeight="600" textAnchor="middle">
        Los 2 números después del @
      </text>
      <rect x="60" y="125" width="200" height="40" fill="#fff" stroke="#d0d4d8" rx="8" />
      <circle cx="80" cy="145" r="3" fill="#5cd4b0" />
      <text x="95" y="149" fontFamily="Poppins, sans-serif" fontSize="10" fill="#4e5357">click derecho → coordenadas</text>
    </svg>
  );
}

function SVGPanelPlusCode() {
  return (
    <svg width="100%" viewBox="0 0 320 200" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: 360 }}>
      <rect width="320" height="200" fill="#f3f4f4" rx="8" />
      <rect x="30" y="20" width="180" height="160" fill="#fff" stroke="#d0d4d8" rx="6" />
      <text x="45" y="42" fontFamily="Poppins, sans-serif" fontSize="11" fill="#2f5f9e" fontWeight="700">Beep México</text>
      <text x="45" y="58" fontFamily="Poppins, sans-serif" fontSize="9" fill="#8a8f95">Empresa de CCTV</text>
      <line x1="45" y1="72" x2="195" y2="72" stroke="#e6e8ea" />
      <rect x="45" y="85" width="16" height="16" fill="none" stroke="#8a8f95" strokeWidth="1.5" />
      <path d="M 53 85 L 61 93 L 53 101 L 45 93 Z" fill="#8a8f95" />
      <text x="68" y="97" fontFamily="Poppins, sans-serif" fontSize="10" fill="#e88b3f" fontWeight="700">7RMP+C3 CDMX</text>
      <path d="M 225 100 L 210 93 M 225 100 L 210 107 M 225 100 L 245 100" stroke="#e88b3f" strokeWidth="2" fill="none" strokeLinecap="round" />
      <text x="255" y="96" fontFamily="Poppins, sans-serif" fontSize="11" fill="#2f5f9e" fontWeight="600">Plus</text>
      <text x="255" y="110" fontFamily="Poppins, sans-serif" fontSize="11" fill="#2f5f9e" fontWeight="600">Code</text>
      <rect x="45" y="115" width="16" height="16" fill="none" stroke="#8a8f95" strokeWidth="1.5" rx="8" />
      <text x="68" y="127" fontFamily="Poppins, sans-serif" fontSize="9" fill="#8a8f95">Av. Reforma 123</text>
      <rect x="45" y="140" width="16" height="16" fill="none" stroke="#8a8f95" strokeWidth="1.5" rx="2" />
      <text x="68" y="152" fontFamily="Poppins, sans-serif" fontSize="9" fill="#8a8f95">www.beepmx.com</text>
    </svg>
  );
}

function SVGBotonCompartir() {
  return (
    <svg width="100%" viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: 360 }}>
      <rect width="320" height="180" fill="#f3f4f4" rx="8" />
      <rect x="30" y="30" width="260" height="120" fill="#fff" stroke="#d0d4d8" rx="6" />
      <rect x="45" y="50" width="70" height="28" fill="#eaf2fb" stroke="#c9d6e3" rx="14" />
      <path d="M 60 64 L 70 58 M 60 64 L 70 70 M 60 64 L 80 64" stroke="#2f5f9e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <text x="85" y="68" fontFamily="Poppins, sans-serif" fontSize="10" fill="#2f5f9e" fontWeight="600">Direcciones</text>
      <rect x="125" y="50" width="55" height="28" fill="#fff4e5" stroke="#e88b3f" strokeWidth="2" rx="14" />
      <circle cx="138" cy="64" r="3" fill="#e88b3f" />
      <circle cx="152" cy="58" r="3" fill="#e88b3f" />
      <circle cx="152" cy="70" r="3" fill="#e88b3f" />
      <line x1="140" y1="62" x2="150" y2="59" stroke="#e88b3f" strokeWidth="1.5" />
      <line x1="140" y1="66" x2="150" y2="69" stroke="#e88b3f" strokeWidth="1.5" />
      <text x="160" y="68" fontFamily="Poppins, sans-serif" fontSize="10" fill="#e88b3f" fontWeight="700">Compartir</text>
      <path d="M 150 95 L 150 115 L 140 108 M 150 115 L 160 108" stroke="#e88b3f" strokeWidth="2" fill="none" strokeLinecap="round" />
      <text x="160" y="135" fontFamily="Poppins, sans-serif" fontSize="10" fill="#2f5f9e" fontWeight="600" textAnchor="middle">Click aquí</text>
    </svg>
  );
}
